import { onSettings, settings } from './settings';

/**
 * Dźwięki syntezowane w Web Audio — bez plików, bez licencji.
 * Zastępcze; docelowo nagrane/wygenerowane efekty, ten sam interfejs.
 */
class Sfx {
  private ctx: AudioContext | null = null;
  private master: GainNode | null = null;
  private noise: AudioBuffer | null = null;
  private lastHit = 0;
  private lastHurt = 0;
  private lastPickup = 0;
  private lastCoin = 0;
  private lastWindup = 0;

  constructor() {
    // Przeglądarka pozwala włączyć dźwięk dopiero po geście gracza.
    const unlock = () => {
      this.ensure();
      void this.ctx?.resume();
    };
    window.addEventListener('pointerdown', unlock);
    window.addEventListener('keydown', unlock);
    onSettings((s) => {
      if (this.master) this.master.gain.value = s.volume;
    });
  }

  private ensure(): AudioContext | null {
    if (this.ctx) return this.ctx;
    try {
      this.ctx = new AudioContext();
    } catch {
      return null;
    }
    this.master = this.ctx.createGain();
    this.master.gain.value = settings.volume;
    this.master.connect(this.ctx.destination);
    const len = this.ctx.sampleRate;
    this.noise = this.ctx.createBuffer(1, len, this.ctx.sampleRate);
    const data = this.noise.getChannelData(0);
    for (let i = 0; i < len; i++) data[i] = Math.random() * 2 - 1;
    return this.ctx;
  }

  private tone(freq: number, dur: number, opts: { type?: OscillatorType; gain?: number; delay?: number; slide?: number } = {}): void {
    const ctx = this.ensure();
    if (!ctx || !this.master || settings.volume <= 0) return;
    const t = ctx.currentTime + (opts.delay ?? 0);
    const osc = ctx.createOscillator();
    const g = ctx.createGain();
    osc.type = opts.type ?? 'sine';
    osc.frequency.setValueAtTime(freq, t);
    if (opts.slide) osc.frequency.exponentialRampToValueAtTime(freq * opts.slide, t + dur);
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(opts.gain ?? 0.2, t + 0.01);
    g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
    osc.connect(g).connect(this.master);
    osc.start(t);
    osc.stop(t + dur + 0.05);
  }

  private burst(dur: number, opts: { freq: number; q?: number; gain?: number; delay?: number; sweep?: number; type?: BiquadFilterType }): void {
    const ctx = this.ensure();
    if (!ctx || !this.master || !this.noise || settings.volume <= 0) return;
    const t = ctx.currentTime + (opts.delay ?? 0);
    const src = ctx.createBufferSource();
    src.buffer = this.noise;
    const filter = ctx.createBiquadFilter();
    filter.type = opts.type ?? 'bandpass';
    filter.frequency.setValueAtTime(opts.freq, t);
    if (opts.sweep) filter.frequency.exponentialRampToValueAtTime(opts.freq * opts.sweep, t + dur);
    filter.Q.value = opts.q ?? 1;
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(opts.gain ?? 0.3, t + 0.02);
    g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
    src.connect(filter).connect(g).connect(this.master);
    src.start(t, Math.random() * 0.5);
    src.stop(t + dur + 0.05);
  }

  click(): void {
    this.tone(880, 0.07, { type: 'triangle', gain: 0.12, slide: 0.75 });
  }

  hover(): void {
    this.tone(1400, 0.03, { gain: 0.03 });
  }

  equip(): void {
    this.burst(0.12, { freq: 3500, q: 4, gain: 0.18 });
    this.tone(520, 0.18, { type: 'triangle', gain: 0.1, delay: 0.02 });
    this.tone(780, 0.22, { type: 'triangle', gain: 0.07, delay: 0.06 });
  }

  /** Przywołanie: narastający szum i wznoszący się ton. */
  summon(): void {
    this.burst(0.9, { freq: 300, sweep: 8, q: 2, gain: 0.25 });
    this.tone(180, 0.9, { type: 'sawtooth', gain: 0.04, slide: 4 });
  }

  /** Odkrycie karty — im rzadsza, tym bogatszy akord. */
  reveal(tier: number): void {
    const chords = [[523], [523, 659], [523, 659, 784], [523, 659, 784, 1047, 1319]];
    (chords[Math.min(tier, 3)] ?? [523]).forEach((f, i) => this.tone(f, 0.5 + tier * 0.2, { type: 'triangle', gain: 0.1, delay: i * 0.06 }));
    if (tier >= 3) this.burst(1.2, { freq: 6000, q: 0.7, gain: 0.08, type: 'highpass' });
  }

  levelUp(): void {
    [523, 659, 784, 1047].forEach((f, i) => this.tone(f, 0.35, { type: 'square', gain: 0.05, delay: i * 0.09 }));
    this.tone(1047, 0.9, { type: 'triangle', gain: 0.1, delay: 0.36 });
  }

  /** Trafienie wroga — ograniczone, żeby setka trafień nie zagłuszyła wszystkiego. */
  hit(crit: boolean): void {
    const ctx = this.ensure();
    if (!ctx) return;
    if (ctx.currentTime - this.lastHit < 0.045) return;
    this.lastHit = ctx.currentTime;
    this.burst(crit ? 0.16 : 0.09, { freq: crit ? 900 : 500, q: 1.5, gain: crit ? 0.22 : 0.12, type: 'lowpass' });
    if (crit) this.tone(1200, 0.12, { type: 'triangle', gain: 0.06, slide: 0.6 });
  }

  hurt(): void {
    const now = performance.now();
    if (now - this.lastHurt < 350) return;
    this.lastHurt = now;
    this.tone(160, 0.18, { type: 'sawtooth', gain: 0.06, slide: 0.6 });
  }

  pickup(): void {
    const now = performance.now();
    if (now - this.lastPickup < 60) return;
    this.lastPickup = now;
    this.tone(1568, 0.08, { gain: 0.05, slide: 1.3 });
  }

  /** Moneta: krótki, metaliczny dwudźwięk (ograniczony, bo ze skrzyni sypie się ich kilka naraz). */
  coin(): void {
    const now = performance.now();
    if (now - this.lastCoin < 55) return;
    this.lastCoin = now;
    const f = 1900 + Math.random() * 300;
    this.tone(f, 0.09, { type: 'square', gain: 0.025 });
    this.tone(f * 1.5, 0.16, { type: 'triangle', gain: 0.05, delay: 0.05 });
  }

  potion(): void {
    [440, 554, 660].forEach((f, i) => this.tone(f, 0.25, { type: 'sine', gain: 0.08, delay: i * 0.05, slide: 1.2 }));
  }

  /** Otwarcie skrzyni: skrzypnięcie wieka i brzęk złota. */
  chest(): void {
    this.burst(0.3, { freq: 280, q: 6, gain: 0.25, sweep: 1.8 });
    [1318, 1568, 1976, 2637].forEach((f, i) => this.tone(f, 0.3, { type: 'triangle', gain: 0.06, delay: 0.18 + i * 0.05 }));
  }

  /** Wybuch płonącej czaszki: niski huk. */
  explosion(): void {
    this.burst(0.6, { freq: 160, q: 0.7, gain: 0.4, sweep: 0.3, type: 'lowpass' });
    this.tone(70, 0.5, { type: 'sine', gain: 0.25, slide: 0.5 });
  }

  /** Przybycie bossa: głęboki, narastający ryk. */
  bossRoar(): void {
    this.tone(55, 1.6, { type: 'sawtooth', gain: 0.12, slide: 1.5 });
    this.tone(82, 1.4, { type: 'sawtooth', gain: 0.08, slide: 1.3, delay: 0.1 });
    this.burst(1.6, { freq: 220, q: 1, gain: 0.25, sweep: 3 });
  }

  /** Zamach wroga przed atakiem — cichy świst (ograniczony). */
  windup(): void {
    const now = performance.now();
    if (now - this.lastWindup < 200) return;
    this.lastWindup = now;
    this.burst(0.25, { freq: 900, q: 3, gain: 0.06, sweep: 2.5 });
  }

  gameOver(): void {
    [392, 311, 262, 196].forEach((f, i) => this.tone(f, 0.6, { type: 'triangle', gain: 0.1, delay: i * 0.22 }));
  }
}

export const sfx = new Sfx();
